import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsBackend, nextAnalyticsTouch, type AnalyticsTouch } from '../lib/site-analytics';

/** One small visible-tab heartbeat; the canister calculates all public counts. */
export function SiteActivity() {
  const { pathname } = useLocation();
  const route = useRef(pathname), previous = useRef<string | null>(null);
  const navigation = useRef(true), inFlight = useRef(false);
  const wake = useRef<(() => Promise<void>) | null>(null);
  const pending = useRef<{ route: string; request: AnalyticsTouch } | null>(null);
  route.current = pathname;
  useEffect(() => {
    if (previous.current !== pathname) { navigation.current = true; previous.current = pathname; void wake.current?.(); }
  }, [pathname]);
  useEffect(() => {
    let alive = true;
    const send = async () => {
      if (!alive || inFlight.current || document.visibilityState !== 'visible') return;
      inFlight.current = true;
      const isNavigation = navigation.current, capturedRoute = route.current;
      try {
        if (!pending.current || pending.current.route !== capturedRoute || pending.current.request.visit !== isNavigation) pending.current = { route: capturedRoute, request: nextAnalyticsTouch(isNavigation) };
        const request = pending.current.request;
        const result = await (await analyticsBackend()).analytics_touch(request);
        if ('ok' in result && route.current === capturedRoute) { navigation.current = false; pending.current = null; if (isNavigation) window.dispatchEvent(new Event('soonscreener:analytics-updated')); }
      } catch { /* Keep browsing available when telemetry is unavailable. */ }
      finally { inFlight.current = false; if (alive && route.current !== capturedRoute) queueMicrotask(() => void send()); }
    };
    wake.current = send;
    // Deferring avoids a duplicate first visit during React's development remount.
    const first = setTimeout(() => void send(), 800);
    const interval = setInterval(() => void send(), 30_000);
    const visibility = () => { if (document.visibilityState === 'visible') void send(); };
    document.addEventListener('visibilitychange', visibility);
    return () => { alive = false; wake.current = null; clearTimeout(first); clearInterval(interval); document.removeEventListener('visibilitychange', visibility); };
  }, []);
  return null;
}
